import { useInternalEditorState } from "@circle-react-shared/uikit/TipTap/internalEditorState";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useEditor } from "@tiptap/react";
import { debounce, isEqual, isFunction } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";
import { useFrameDocument } from "@/react/hooks/utils/useFrameDocument";

import { getTiptapExtensions } from "./Extensions";
import type { TiptapEditorProps } from "./TiptapEditorProps.types";
import { clipboardTextParser } from "./utilities/clipboardTextParser";
import {
  editorHasNoMentionOrEntityOrMessage,
  sanitizeContent,
  sanitizeInputJson,
} from "./utilities/sanitizeContent";
import {
  generateLocalSgidToObjectMap,
  getSgidValuesFromHTML,
} from "./utilities/sgidForEditorContent";
import { useEditorLocalState } from "./utilities/useEditorLocalState";

interface UseTiptapEditorParams {
  autofocus: boolean;
  chatProps: NonNullable<TiptapEditorProps["chatProps"]>;
  editable: boolean;
  editorClassName?: TiptapEditorProps["editorClassName"];
  maxLength?: TiptapEditorProps["maxLength"];
  menubarProps: TiptapEditorProps["menubarProps"];
  onBlur?: TiptapEditorProps["onBlur"];
  onChange?: TiptapEditorProps["onChange"];
  onFocus?: TiptapEditorProps["onFocus"];
  placeholder: string;
  portalTargetId?: TiptapEditorProps["portalTargetId"];
  rich_text_body: TiptapEditorProps["rich_text_body"];
  shouldDisableEntities: boolean;
  shouldDisableMentions: boolean;
  shouldInsertLineBreakOnEnter: boolean;
  shouldLimitEntitiesToSpaces: boolean;
  shouldSupportCodeBlocks: boolean;
  shouldSupportHeadings: boolean;
  shouldSupportTables: boolean;
  type: NonNullable<TiptapEditorProps["type"]>;
  value?: TiptapEditorProps["value"];
}

export const useTiptapEditor = ({
  autofocus,
  chatProps,
  editable,
  editorClassName,
  maxLength,
  menubarProps,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  portalTargetId,
  rich_text_body,
  shouldDisableEntities,
  shouldDisableMentions,
  shouldInsertLineBreakOnEnter,
  shouldLimitEntitiesToSpaces,
  shouldSupportCodeBlocks,
  shouldSupportHeadings,
  shouldSupportTables,
  type,
  value,
}: UseTiptapEditorParams) => {
  // Initial content and persisted editor metadata
  const isWorkflowType = type === "workflow";
  const richTextBody =
    editable && isWorkflowType
      ? {
          body: value?.body || value,
          sgids_to_object_map: value?.localSgidToObjectMap || {},
        }
      : rich_text_body;

  const {
    body: initialValue,
    attachments = [],
    sgids_to_object_map: sgidToObjectMap = {},
  } = richTextBody || {};

  // A workflow draft keeps its dismissed link previews on the form value, while
  // every other surface reads them back from the saved message metadata.
  const isWorkflowDraft = editable && isWorkflowType;
  const initialLinkPreviews: Record<string, boolean> =
    (isWorkflowDraft ? value?.unfurlUrls : chatProps.unfurlUrls) || {};
  const [linkPreviews, setLinkPreviews] =
    useState<Record<string, boolean>>(initialLinkPreviews);

  const { arePopupsVisible } = useInternalEditorState();
  const { frameDocument, isInFrame } = useFrameDocument();

  // Local state shared with TipTap node views
  const {
    isFocused,
    setIsFocused,
    localSgidToObjectMap,
    addToLocalSgidToObjectMap,
    setLocalSgidToObjectMap,
    messageAttachments,
    addMessageAttachments,
    removeMessageAttachments,
    setMessageAttachments,
  } = useEditorLocalState({
    initialValue: sgidToObjectMap,
  });

  const localSgidRef = useRef(localSgidToObjectMap);
  localSgidRef.current = localSgidToObjectMap;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const linkPreviewsRef = useRef(linkPreviews);
  linkPreviewsRef.current = linkPreviews;
  const isWorkflowDraftRef = useRef(isWorkflowDraft);
  isWorkflowDraftRef.current = isWorkflowDraft;

  // Editor change handling
  const debouncedOnChange = useMemo(
    () =>
      debounce(editor => {
        if (!isFunction(onChangeRef.current)) return;
        const json = editor.getJSON();
        onChangeRef.current({
          body: json,
          localSgidToObjectMap: localSgidRef.current,
          ...(isWorkflowDraftRef.current
            ? { unfurlUrls: linkPreviewsRef.current }
            : {}),
        });
      }, 500),
    [],
  );

  useEffect(() => () => debouncedOnChange.cancel(), [debouncedOnChange]);

  const sanitizedInitialValue = useMemo(() => {
    if (isWorkflowType) {
      return sanitizeInputJson(value?.body || value || initialValue);
    }

    return sanitizeInputJson(value || initialValue);
  }, [isWorkflowType, value, initialValue]);

  // TipTap editor instance
  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    editable,
    content: sanitizedInitialValue,
    onUpdate: ({ editor }) => {
      sanitizeContent({ editor });
      debouncedOnChange(editor);
    },
    onFocus: () => {
      setIsFocused(true);
      isFunction(onFocus) && onFocus();
    },
    onBlur: () => {
      setIsFocused(false);
      isFunction(onBlur) && onBlur();
    },
    autofocus,
    extensions: getTiptapExtensions({
      placeholder,
      maxLength,
      menubarProps,
      type,
      shouldDisableEntities,
      shouldDisableMentions,
      shouldInsertLineBreakOnEnter,
      shouldLimitEntitiesToSpaces,
      shouldSupportCodeBlocks,
      shouldSupportHeadings,
      shouldSupportTables,
      portalTargetId,
      frameDocument,
    }),
    editorProps: {
      transformPastedHTML(html: string) {
        const sanitizedHTML = html.replace(
          /<edited-label[^>]*>.*?<\/edited-label>/gi,
          "",
        );

        const sgids: string[] = getSgidValuesFromHTML(sanitizedHTML);
        if (sgids.length) {
          void generateLocalSgidToObjectMap({
            sgids,
            setLocalSgidToObjectMap,
          });
        }
        return sanitizedHTML;
      },
      clipboardTextParser,
      attributes: {
        class: classNames(
          editorClassName,
          anchorClassNames("tiptap-editor"),
          !editable && legacyClassNames("readonly"),
        ),
      },
    },
  });

  useEffect(() => {
    if (isWorkflowDraft) return;

    if (editor?.isEditable) {
      setLinkPreviews(chatProps.unfurlUrls ? chatProps.unfurlUrls : {});
    } else {
      setLinkPreviews(chatProps.unfurlUrls || {});
    }
  }, [editor?.isEditable, chatProps.unfurlUrls, isWorkflowDraft]);

  // Dismissing a link preview leaves the document untouched, so the workflow
  // form value has to be refreshed outside the editor's own update cycle.
  const emittedLinkPreviewsRef = useRef(initialLinkPreviews);

  useEffect(() => {
    if (!editor || !isWorkflowDraft) return;
    if (!isFunction(onChangeRef.current)) return;
    if (isEqual(emittedLinkPreviewsRef.current, linkPreviews)) return;

    emittedLinkPreviewsRef.current = linkPreviews;
    onChangeRef.current({
      body: editor.getJSON(),
      localSgidToObjectMap: localSgidRef.current,
      unfurlUrls: linkPreviews,
    });
  }, [editor, isWorkflowDraft, linkPreviews]);

  // Content state helpers
  const editorHasNoText = () =>
    editor?.state?.doc?.textContent.trim().length === 0;

  const editorHasNoContent = () =>
    editorHasNoMentionOrEntityOrMessage(editor) &&
    (editor?.isEmpty || editorHasNoText());

  return {
    addMessageAttachments,
    addToLocalSgidToObjectMap,
    arePopupsVisible,
    attachments,
    editor,
    editorHasNoContent,
    isFocused,
    isInFrame,
    linkPreviews,
    localSgidToObjectMap,
    messageAttachments,
    removeMessageAttachments,
    sanitizedInitialValue,
    setLinkPreviews,
    setLocalSgidToObjectMap,
    setMessageAttachments,
    sgidToObjectMap,
  };
};
